/**
 * The Ask modal for one of a case's outstanding items (kanban 52a4147a),
 * opened from the case file's row (the "Never asked" rows that also opened
 * it from the "Who we've asked" panel were merged into that row, UX round
 * 2026-11-03). After a send the door's asks are
 * refetched as well as patched, so the "Merlin drafted … press Send" banner
 * and the chat's chips catch up with drafts the send approved.
 */
import { AskModal } from '../../components/quality/AskModal';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import type {
  DoorCaseTaskRead,
  DoorInvestigationRead,
  DoorThreadRead,
} from '../../services/qcpInvestigations';
import { briefPlan, taskAskPlan } from './briefPlan';

export interface TaskAskModalProps {
  investigation: Pick<DoorInvestigationRead, 'id' | 'investigation_no'>;
  task: DoorCaseTaskRead;
  /** Merlin's chat drafts in the case's scope (sent along as draft_ids). */
  chatDrafts: DoorDataRequestRead[];
  /** The thread behind the case, when there is one: the item then opens on
   *  the Do next step it restates and Merlin's draft for it (briefPlan). */
  thread?: DoorThreadRead | null;
  /** The door's asks on this case, for that draft. */
  asks?: ReadonlyArray<DoorDataRequestRead>;
  viewerName: string | null;
  onUpdated: (request: DoorDataRequestRead) => void;
  /** Refetch the door's asks once the send is through. */
  onRefresh: () => void;
  onClose: () => void;
}

export function TaskAskModal({
  investigation,
  task,
  chatDrafts,
  thread = null,
  asks = [],
  viewerName,
  onUpdated,
  onRefresh,
  onClose,
}: TaskAskModalProps) {
  const doNext = thread ? briefPlan(thread, investigation).doNext : [];
  const plan = taskAskPlan(task, thread, doNext, asks);
  return (
    <AskModal
      key={`task-${task.id}`}
      scope={{
        kind: 'case',
        id: investigation.id,
        no: investigation.investigation_no,
        task: { id: task.id, label: task.label },
      }}
      desk={plan.desk}
      stepNo={plan.stepNo}
      steps={plan.steps}
      drafts={plan.drafts}
      chatDrafts={chatDrafts}
      viewerName={viewerName}
      onClose={onClose}
      onSent={(result) => {
        [...result.sent, ...result.discarded].forEach(onUpdated);
        onRefresh();
        onClose();
      }}
      onDiscarded={(discarded) => {
        discarded.forEach(onUpdated);
        onClose();
      }}
    />
  );
}

export default TaskAskModal;
